/**
 * The words of the Approvals screen (audits/2026-10-26/approval-queue-spec.md, §7). Every
 * sentence about what Approve does says it as this console's; the reasons a row carries are
 * the proposer's, shown as written, and the route's answer is shown as the route gave it.
 */
import type { Approval } from "./api.ts";

export const APPROVALS_TITLE = "Approvals";

export const APPROVALS_ABOUT =
	"Writes the advisor proposes for this ad account. Nothing here has been sent to X: a proposal is sent only when someone who may write the account approves it, and then as that person.";

/** 🔴 Beside Approve: what the click does, in this console's words. */
export const APPROVE_SENDS =
	"Approve sends this write to X as you — the same request, and the same checks, as doing it by hand. If X does not answer, the row says unknown and is never sent again: read X before doing anything else.";

export const NOT_YOUR_OWN =
	"A proposal is approved by someone other than its author; its author may reject it.";

export const QUEUE_UNAVAILABLE = "The approval queue is not set up yet.";

export const QUEUE_EMPTY = "Nothing is waiting for approval on this account.";

export const HISTORY_HEADING = "Decided and expired";

/** «Decided and expired» with no row: what will be here, and from where (3d, 30.10). */
export const HISTORY_EMPTY =
	"Nothing decided or expired on this account yet. A request moves here from Waiting once someone approves or rejects it, or once it expires.";

/** The right-hand panel before a row is picked (3d, 30.10). */
export const NOTHING_CHOSEN =
	"Pick a request in Waiting: what it would change, and its Approve and Reject, show here.";

/** The route a row names, as the action it proposes. */
export function actionWords(a: Pick<Approval, "route" | "targetId" | "body">): string {
	const id = a.targetId ?? "";
	switch (a.route) {
		case "POST /campaigns/:id/status":
			return a.body.status === "ACTIVE"
				? `Start campaign ${id}`
				: `Set campaign ${id} to ${String(a.body.status)}`;
		case "PUT /line-items/:id":
			return a.body.status === "ACTIVE" ? `Start ad group ${id}` : `Change ad group ${id}`;
		case "PUT /campaigns/:id":
			return `Change campaign ${id}`;
		case "POST /campaigns":
			return "Create a campaign, paused";
		case "POST /line-items":
			return "Create an ad group, paused";
		case "POST /tweets":
			return "Write a tweet for an ad";
		case "POST /cards":
			return "Create a website card";
		case "POST /promoted-tweets":
			return "Attach a tweet to an ad group";
		case "POST /targeting-criteria":
			return "Add a targeting criterion";
		default:
			return a.route;
	}
}

/**
 * The fields the request will send, each beside what the proposer saw on X when it has the
 * same field (X's snake_case name for the contract's camelCase one): «total budget: 10000000 →
 * 13000000». A field with nothing observed is shown as sent. Values are as the row holds them —
 * micro units stay micro units, because converting would need the currency the row does not carry.
 */
export function changeLines(a: Pick<Approval, "body" | "observed">): string[] {
	const snake = (k: string) => k.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
	const words = (k: string) => snake(k).replace(/_/g, " ");
	return Object.entries(a.body)
		.filter(([k]) => k !== "account")
		.map(([k, v]) => {
			const seen = a.observed[snake(k)] ?? a.observed[k];
			return seen === undefined
				? `${words(k)}: ${JSON.stringify(v)}`
				: `${words(k)}: ${JSON.stringify(seen)} → ${JSON.stringify(v)}`;
		});
}

/** Who proposed it, in words. */
export function originWords(a: Pick<Approval, "origin" | "proposedBy">): string {
	return a.origin === "advisor" ? "proposed by the advisor" : `proposed by ${a.proposedBy}`;
}

/** How long a pending row has left, or that it has run out. */
export function expiresWords(expiresAt: string, now: Date): string {
	const left = Date.parse(expiresAt) - now.getTime();
	if (left <= 0) return "expired";
	const hours = Math.floor(left / 3_600_000);
	const minutes = Math.floor((left % 3_600_000) / 60_000);
	return hours > 0 ? `expires in ${hours} h ${minutes} min` : `expires in ${minutes} min`;
}

/** A decided row's status, in words. */
export function statusWords(status: Approval["status"]): string {
	switch (status) {
		case "pending":
			return "waiting";
		case "approving":
			return "being sent";
		case "done":
			return "sent — X answered";
		case "refused":
			return "refused — nothing was written";
		case "unknown":
			return "unknown — X may or may not have written it; read X";
		case "rejected":
			return "rejected";
		case "expired":
			return "expired — never sent";
		case "stale":
			return "stale — X changed since it was proposed; never sent";
		case "over_limit":
			return "over the agent's limit — never sent";
	}
}

/**
 * The route's answer after Approve, in one line: its HTTP status, and its `error` or `created`
 * when the body carries them — the words the route itself chose.
 */
export function resultWords(result: { httpStatus: number; body: unknown } | null): string | null {
	if (result === null) return null;
	const body =
		typeof result.body === "object" && result.body !== null
			? (result.body as Record<string, unknown>)
			: {};
	const parts = [`HTTP ${result.httpStatus}`];
	if (typeof body.error === "string") parts.push(body.error);
	if ("created" in body) parts.push(`created: ${JSON.stringify(body.created)}`);
	return parts.join(" · ");
}
